/**
 * Everything the workspace needs to host video-edit nodes, kept out of the
 * (shared, very long) workspace page: creating nodes, opening the editor,
 * exporting + uploading, and the edges that draw their lineage.
 */
import React, { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { CsAsset } from '../../../../common/models/content-studio.model';
import {
    CanvasGenerationNode,
    CanvasNode,
    CanvasVideoEditNode,
} from '../canvas/canvas-graph.types';
import { GENERATION_NODE_WIDTH } from '../canvas/GenerationNode';
import { VIDEO_EDIT_NODE_WIDTH } from '../canvas/VideoEditNode';
import {
    AspectRatio,
    VideoComposition,
    createEmptyComposition,
    createMainClip,
    insertMainClips,
    mainTrack,
    normalizeComposition,
} from './composition';
import { applyTemplate, TemplateMedia } from './templates';
import { exportComposition } from './export';
import { loadVideoBrand } from './brand';
import { VideoEditorModal, VideoExportState } from './VideoEditorModal';

const NODE_EST_H = 360;

export interface VideoEditCanvasDeps {
    projectUuid?: string;
    nodesRef: React.MutableRefObject<CanvasNode[]>;
    setNodes: React.Dispatch<React.SetStateAction<CanvasNode[]>>;
    pushHistory: () => void;
    findFreeSpawn: (x: number, y: number, w: number, h: number) => { x: number; y: number };
    focusNode: (x: number, y: number, w: number, h: number) => void;
    select: (ids: string[]) => void;
    /** Canvas coordinates of the middle of the view, for nodes made from nothing. */
    viewCenter: () => { x: number; y: number };
}

export interface CsEdgeLike {
    fromId: string;
    toId: string;
    state: 'active' | 'completed' | 'failed';
}

/** Closest editor aspect to a picture's shape. */
export function aspectForSize(width?: number, height?: number): AspectRatio {
    if (!width || !height) return '9:16';
    const r = width / height;
    if (r < 0.68) return '9:16';
    if (r < 0.9) return '4:5';
    if (r < 1.4) return '1:1';
    return '16:9';
}

function assetToTemplateMedia(a: CsAsset): TemplateMedia {
    return {
        src: a.url!,
        kind: a.kind === 'video' ? 'video' : 'image',
        assetUuid: a.uuid,
        thumbnailUrl: a.thumbnailUrl || (a.kind === 'image' ? a.url : undefined),
        durationSec: a.dimensions?.durationSec,
    };
}

export function useVideoEditCanvas(deps: VideoEditCanvasDeps) {
    const api = useContentStudioApi();
    const depsRef = useRef(deps);
    depsRef.current = deps;
    const [openNodeId, setOpenNodeId] = useState<string | null>(null);
    const [exportStates, setExportStates] = useState<Record<string, VideoExportState | null>>({});
    const controllers = useRef<Record<string, AbortController>>({});

    const setExport = useCallback((id: string, s: VideoExportState | null) => {
        setExportStates((prev) => ({ ...prev, [id]: s }));
    }, []);

    const getNode = (id: string) =>
        depsRef.current.nodesRef.current.find((n) => n.id === id && n.kind === 'video-edit') as CanvasVideoEditNode | undefined;

    const patchNode = useCallback((id: string, patch: Partial<CanvasVideoEditNode>) => {
        depsRef.current.setNodes((prev) => prev.map((n) => (n.id === id && n.kind === 'video-edit' ? { ...n, ...patch } : n)));
    }, []);

    /** Drop a video-edit node on the canvas. Returns its id. */
    const createNode = useCallback((composition: VideoComposition, opts: { anchor?: { x: number; y: number }; sourceNodeIds?: string[]; open?: boolean } = {}) => {
        const d = depsRef.current;
        const id = `ved-${uuidv4().slice(0, 8)}`;
        const at = opts.anchor ?? (() => {
            const c = d.viewCenter();
            return { x: c.x - VIDEO_EDIT_NODE_WIDTH / 2, y: c.y - NODE_EST_H / 2 };
        })();
        const pos = d.findFreeSpawn(at.x, at.y, VIDEO_EDIT_NODE_WIDTH, NODE_EST_H);
        const node: CanvasVideoEditNode = {
            kind: 'video-edit',
            id,
            x: pos.x,
            y: pos.y,
            composition: normalizeComposition(composition),
            sourceNodeIds: opts.sourceNodeIds?.length ? opts.sourceNodeIds : undefined,
            generationNodeIds: [],
        };
        d.pushHistory();
        d.setNodes((prev) => [...prev, node]);
        d.select([id]);
        d.focusNode(pos.x, pos.y, VIDEO_EDIT_NODE_WIDTH, NODE_EST_H);
        if (opts.open) setOpenNodeId(id);
        return id;
    }, []);

    /** Start a timeline from generation nodes, in the order given, with a template applied. */
    const createFromGenerations = useCallback(async (sources: CanvasGenerationNode[]) => {
        const ready = sources.filter((s) => s.asset?.status === 'ready' && s.asset.url);
        const brand = await loadVideoBrand(api.getBrandKit);
        const first = ready[0]?.asset;
        const aspect = aspectForSize(first?.dimensions?.width, first?.dimensions?.height);
        const composition = ready.length
            ? applyTemplate('slow-showcase', ready.map((s) => assetToTemplateMedia(s.asset)), { aspect, brand })
            : createEmptyComposition(aspect);
        const anchorNode = ready[0];
        return createNode(composition, {
            anchor: anchorNode ? { x: anchorNode.x + GENERATION_NODE_WIDTH + 80, y: anchorNode.y } : undefined,
            sourceNodeIds: ready.map((s) => s.id),
            open: true,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createNode]);

    /** Append one generation to the newest timeline on the canvas (or start one) and open it. */
    const addToTimeline = useCallback((gen: CanvasGenerationNode) => {
        const a = gen.asset;
        if (!a?.url) return;
        const timelines = depsRef.current.nodesRef.current.filter((n) => n.kind === 'video-edit') as CanvasVideoEditNode[];
        const target = timelines[timelines.length - 1];
        const clip = createMainClip({
            kind: a.kind === 'video' ? 'video' : 'image',
            src: a.url,
            assetUuid: a.uuid,
            thumbnailUrl: a.thumbnailUrl || (a.kind === 'image' ? a.url : undefined),
            sourceDuration: a.dimensions?.durationSec,
            duration: a.kind === 'video' ? Math.min(a.dimensions?.durationSec ?? 5, 8) : 3,
        });
        if (!target) {
            const comp = insertMainClips(createEmptyComposition(aspectForSize(a.dimensions?.width, a.dimensions?.height)), [clip]);
            createNode(comp, { anchor: { x: gen.x + GENERATION_NODE_WIDTH + 80, y: gen.y }, sourceNodeIds: [gen.id], open: true });
            return;
        }
        const hasClips = mainTrack(target.composition).clips.length > 0;
        const next = insertMainClips(target.composition, [hasClips ? { ...clip, transitionIn: { type: 'crossfade', duration: 0.5 } } : clip]);
        depsRef.current.pushHistory();
        patchNode(target.id, {
            composition: next,
            sourceNodeIds: Array.from(new Set([...(target.sourceNodeIds || []), gen.id])),
        });
        setOpenNodeId(target.id);
    }, [createNode, patchNode]);

    /** Render, upload and land the result on the canvas. Resolves to the saved asset's url. */
    const exportNode = useCallback(async (nodeId: string, override?: VideoComposition): Promise<string> => {
        const node = getNode(nodeId);
        const projectUuid = depsRef.current.projectUuid;
        if (!node || !projectUuid) throw new Error('Unknown video node');
        if (controllers.current[nodeId]) throw new Error('Already exporting');
        const composition = normalizeComposition(override ?? node.composition);
        if (override) patchNode(nodeId, { composition });
        const controller = new AbortController();
        controllers.current[nodeId] = controller;
        let last = 0;
        setExport(nodeId, { status: 'running', progress: { phase: 'prepare', progress: 0 } });
        try {
            const result = await exportComposition(composition, {
                signal: controller.signal,
                onProgress: (p) => {
                    const now = performance.now();
                    // Re-rendering the canvas on every frame would slow the export it reports.
                    if (now - last < 120 && p.progress < 1) return;
                    last = now;
                    setExport(nodeId, { status: 'running', progress: { ...p, progress: p.progress * 0.92 } });
                },
            });
            if (controller.signal.aborted) throw new DOMException('cancelled', 'AbortError');
            setExport(nodeId, { status: 'running', progress: { phase: 'upload', progress: 0.94 } });
            const file = new File([result.blob], `video-${nodeId}.${result.extension}`, { type: result.mimeType });
            const asset = await api.importVideo(projectUuid, file, {
                thumbnail: result.thumbnail,
                width: result.width,
                height: result.height,
                durationSec: result.durationSec,
                origin: 'editor',
            });
            const fresh = getNode(nodeId) ?? node;
            const genId = `gen-${asset.uuid}`;
            const d = depsRef.current;
            const pos = d.findFreeSpawn(fresh.x + VIDEO_EDIT_NODE_WIDTH + 80, fresh.y, GENERATION_NODE_WIDTH, 300);
            const gen: CanvasGenerationNode = {
                kind: 'generation',
                id: genId,
                x: pos.x,
                y: pos.y,
                asset,
                recipeNodeId: nodeId,
                approved: true,
            };
            d.pushHistory();
            d.setNodes((prev) => [
                ...prev.map((n) => (n.id === nodeId && n.kind === 'video-edit'
                    ? {
                        ...n,
                        renderedAssetUuid: asset.uuid,
                        renderedUrl: asset.url,
                        renderedThumbnailUrl: asset.thumbnailUrl,
                        generationNodeIds: [...(n.generationNodeIds || []), genId],
                    }
                    : n)),
                gen,
            ]);
            setExport(nodeId, { status: 'done', url: asset.url, progress: { phase: 'finalize', progress: 1 } });
            return asset.url as string;
        } catch (err: any) {
            if (err?.name === 'AbortError' || controller.signal.aborted) {
                setExport(nodeId, null);
            } else {
                // eslint-disable-next-line no-console
                console.error('[video-editor] export failed', err);
                setExport(nodeId, { status: 'error', error: typeof err?.message === 'string' ? err.message : undefined });
            }
            throw err;
        } finally {
            delete controllers.current[nodeId];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patchNode, setExport]);

    const cancelExport = useCallback((nodeId: string) => {
        controllers.current[nodeId]?.abort();
    }, []);

    /** Lineage: the shots a timeline was started from, and what each export produced. */
    const edgesFor = useCallback((nodes: CanvasNode[]): CsEdgeLike[] => {
        const out: CsEdgeLike[] = [];
        const ids = new Set(nodes.map((n) => n.id));
        for (const n of nodes) {
            if (n.kind !== 'video-edit') continue;
            const running = exportStates[n.id]?.status === 'running';
            for (const src of n.sourceNodeIds || []) {
                if (ids.has(src)) out.push({ fromId: src, toId: n.id, state: running ? 'active' : 'completed' });
            }
            for (const g of n.generationNodeIds || []) {
                if (ids.has(g)) out.push({ fromId: n.id, toId: g, state: 'completed' });
            }
        }
        return out;
    }, [exportStates]);

    const openNode = openNodeId ? getNode(openNodeId) : undefined;
    const modal = openNode && deps.projectUuid ? (
        <VideoEditorModal
            key={openNode.id}
            open
            projectUuid={deps.projectUuid}
            initialComposition={openNode.composition}
            onChange={(comp) => patchNode(openNode.id, { composition: comp })}
            onClose={(comp) => {
                patchNode(openNode.id, { composition: normalizeComposition(comp) });
                setOpenNodeId(null);
            }}
            onExport={(comp) => { void exportNode(openNode.id, comp).catch(() => undefined); }}
            exportState={exportStates[openNode.id] ?? null}
            onCancelExport={() => cancelExport(openNode.id)}
            onDismissExport={() => setExport(openNode.id, null)}
        />
    ) : null;

    return {
        openEditor: setOpenNodeId,
        openNodeId,
        createNode,
        createFromGenerations,
        addToTimeline,
        exportNode,
        cancelExport,
        exportStates,
        dismissExport: (id: string) => setExport(id, null),
        edgesFor,
        modal,
    };
}
